import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Loader2, Lock, CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState("idle"); // idle | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setErrorMsg("This reset link is missing a token. Open the link from your email or request a new reset.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirmPwd) { setErrorMsg("Passwords do not match"); return; }
    if (pwd.length < 8) { setErrorMsg("Password must be at least 8 characters"); return; }
    setBusy(true);
    setErrorMsg("");
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token, new_password: pwd, confirm_password: confirmPwd,
      });
      setState("success");
      setTimeout(() => nav("/"), 3000);
    } catch (err) {
      setState("error");
      setErrorMsg(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="reset-password-page">
      <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Account</span>
      <h1 className="font-display text-3xl text-[#2C4C3B] mt-1">Reset your password</h1>
      <p className="text-[#52606D] mt-2 text-sm">Set a new password for your CommPass account. Reset links expire after 60 minutes.</p>

      {state === "success" ? (
        <div className="mt-8 p-6 rounded-2xl bg-[#E6F0E9] border border-[#2C4C3B]/15 flex items-start gap-3" data-testid="reset-password-success">
          <CheckCircle2 className="w-5 h-5 text-[#2C4C3B] mt-0.5" />
          <div>
            <p className="text-sm text-[#2C4C3B] font-semibold">Password updated.</p>
            <p className="text-xs text-[#52606D] mt-1">Redirecting you home — sign in with your new password from the header.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52606D]" />
            <Input type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)}
              placeholder="New password (min 8 characters)" className="pl-9 rounded-xl" data-testid="reset-password-input" autoComplete="new-password" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52606D]" />
            <Input type="password" required minLength={8} value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="Confirm new password" className="pl-9 rounded-xl" data-testid="reset-password-confirm-input" autoComplete="new-password" />
          </div>
          {errorMsg && (
            <div className="p-3 rounded-xl bg-[#FDF6F0] border border-[#C66A51]/30 text-sm text-[#9C422A] flex items-start gap-2" data-testid="reset-password-error">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{errorMsg}</span>
            </div>
          )}
          <Button type="submit" disabled={busy || !token}
            className="w-full rounded-full bg-[#2C4C3B] hover:bg-[#1F3A2C] text-white py-6" data-testid="reset-password-submit">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</> : "Set new password"}
          </Button>
        </form>
      )}
    </div>
  );
}
